"use client";

import { getCookie } from "@/utils/cookies";

export type ChatNotificationPreference =
  | "enabled"
  | "disabled"
  | "dismissed";

const TOKEN_STORAGE_KEY = "fosterferret:chat-notifications:token";
const PREFERENCE_STORAGE_PREFIX =
  "fosterferret:chat-notifications:preference";
const PERMISSION_PROMPT_STORAGE_PREFIX =
  "fosterferret:chat-notifications:permission-prompted";

const getPreferenceKey = (userId: string) =>
  `${PREFERENCE_STORAGE_PREFIX}:${userId}`;

const getPermissionPromptKey = (userId: string) =>
  `${PERMISSION_PROMPT_STORAGE_PREFIX}:${userId}`;

const requestDeviceUpdate = async (
  method: "POST" | "DELETE",
  fcmToken: string,
): Promise<void> => {
  const accessToken = getCookie("accessToken");
  const baseUrl = process.env.NEXT_PUBLIC_BASE_URL?.replace(/\/$/, "");
  if (!accessToken || !baseUrl) {
    throw new Error("Authentication is required to update notifications.");
  }

  const response = await fetch(`${baseUrl}/notifications/devices`, {
    method,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ fcmToken }),
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as {
      message?: string;
    } | null;
    throw new Error(payload?.message || "Unable to update chat notifications.");
  }
};

export const registerChatNotificationDevice = (fcmToken: string) =>
  requestDeviceUpdate("POST", fcmToken);

export const unregisterChatNotificationDevice = (fcmToken: string) =>
  requestDeviceUpdate("DELETE", fcmToken);

export const getStoredChatNotificationToken = (): string | null =>
  typeof window === "undefined"
    ? null
    : window.localStorage.getItem(TOKEN_STORAGE_KEY);

export const storeChatNotificationToken = (fcmToken: string): void => {
  window.localStorage.setItem(TOKEN_STORAGE_KEY, fcmToken);
};

export const clearStoredChatNotificationToken = (): void => {
  if (typeof window !== "undefined") {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  }
};

export const getChatNotificationPreference = (
  userId: string,
): ChatNotificationPreference | null => {
  if (typeof window === "undefined") return null;
  const value = window.localStorage.getItem(getPreferenceKey(userId));
  return value === "enabled" || value === "disabled" || value === "dismissed"
    ? value
    : null;
};

export const setChatNotificationPreference = (
  userId: string,
  preference: ChatNotificationPreference,
): void => {
  window.localStorage.setItem(getPreferenceKey(userId), preference);
};

export const hasRequestedChatNotificationPermission = (
  userId: string,
): boolean =>
  typeof window !== "undefined" &&
  window.localStorage.getItem(getPermissionPromptKey(userId)) === "true";

export const markChatNotificationPermissionRequested = (
  userId: string,
): void => {
  window.localStorage.setItem(getPermissionPromptKey(userId), "true");
};
